/**
 * Bidirectional Adjustments Mapping
 *
 * Converts between Sati GPU pipeline preset format and Tank frontend adjustments format.
 * This mirrors the Python implementation in src/contracts/adjustments.py
 *
 * Sati format: Based on src/sati/gpu/presets.py Preset dataclass
 * Tank format: Based on src/tank/src/utils/imageAdjustments.js DEFAULT_ADJUSTMENTS
 */

import { DEFAULT_ADJUSTMENTS } from '../utils/imageAdjustments';

// Conversion constants (must match Python)
const CONTRAST_SCALE = 66.67; // 1.47 in Sati becomes ~31 in Tank
const CONTRAST_MAX_SATI = 1.5; // Maximum contrast boost factor

/**
 * Convert Sati applied_settings to Tank adjustments format.
 *
 * @param {Object} appliedSettings - The applied_settings from meta.json
 * @returns {Object} Tank adjustments object compatible with DEFAULT_ADJUSTMENTS
 */
export function satiToTank(appliedSettings) {
  if (!appliedSettings || !appliedSettings.tank_format) {
    return { ...DEFAULT_ADJUSTMENTS };
  }

  // The tank_format is already in the correct format from Python
  const tank = appliedSettings.tank_format;

  return {
    exposure: tank.exposure ?? 0,
    contrast: tank.contrast ?? 0,
    highlights: tank.highlights ?? 0,
    shadows: tank.shadows ?? 0,
    whites: tank.whites ?? 0,
    blacks: tank.blacks ?? 0,
    clarity: tank.clarity ?? 0,
    temperature: tank.temperature ?? 0,
    tint: tank.tint ?? 0,
    sharpening: {
      amount: tank.sharpening?.amount ?? 0,
      radius: tank.sharpening?.radius ?? 0.5,
    },
    color_grading: {
      lift: {
        r: tank.color_grading?.lift?.r ?? 0,
        g: tank.color_grading?.lift?.g ?? 0,
        b: tank.color_grading?.lift?.b ?? 0,
      },
      gamma: {
        r: tank.color_grading?.gamma?.r ?? 1,
        g: tank.color_grading?.gamma?.g ?? 1,
        b: tank.color_grading?.gamma?.b ?? 1,
      },
      gain: {
        r: tank.color_grading?.gain?.r ?? 1,
        g: tank.color_grading?.gain?.g ?? 1,
        b: tank.color_grading?.gain?.b ?? 1,
      },
    },
    denoise: tank.denoise ?? 0,
  };
}

/**
 * Convert raw Sati preset dict to Tank adjustments (for direct preset loading).
 *
 * Use this when you have a raw preset dict (not from applied_settings).
 * Normally you should use satiToTank() with applied_settings from meta.json.
 *
 * @param {Object} presetDict - Raw Sati preset dictionary
 * @returns {Object} Tank adjustments object
 */
export function satiPresetToTank(presetDict) {
  if (!presetDict) {
    return { ...DEFAULT_ADJUSTMENTS };
  }

  const exposure = presetDict.exposure || {};
  const tonemap = presetDict.tonemap || {};
  const wb = presetDict.white_balance || {};
  const colorBalance = presetDict.color_balance || {};
  const sharpen = presetDict.sharpen || {};
  const denoise = presetDict.denoise || {};

  // Convert tonemap contrast: Sati 1.0 = neutral, 1.47 = default
  const satiContrast = tonemap.contrast ?? 1.0;
  const rawContrast = Math.round((satiContrast - 1.0) * CONTRAST_SCALE);
  const tankContrast = Math.max(-100, Math.min(100, rawContrast));

  // Convert white balance: Sati -1..1 to Tank -100..100
  const tankTemperature = Math.round((wb.temperature ?? 0) * 100);
  const tankTint = Math.round((wb.tint ?? 0) * 100);

  // Convert color balance to color grading
  const shadows = colorBalance.shadows || [1.0, 1.0, 1.0];
  const midtones = colorBalance.midtones || [1.0, 1.0, 1.0];
  const highlights = colorBalance.highlights || [1.0, 1.0, 1.0];

  // Sati shadows -> Tank lift (as offset from 1.0)
  const liftR = Array.isArray(shadows) ? shadows[0] - 1.0 : 0;
  const liftG = Array.isArray(shadows) ? shadows[1] - 1.0 : 0;
  const liftB = Array.isArray(shadows) ? shadows[2] - 1.0 : 0;

  // Sati midtones -> Tank gamma (direct)
  const gammaR = Array.isArray(midtones) ? midtones[0] : 1;
  const gammaG = Array.isArray(midtones) ? midtones[1] : 1;
  const gammaB = Array.isArray(midtones) ? midtones[2] : 1;

  // Sati highlights -> Tank gain (direct)
  const gainR = Array.isArray(highlights) ? highlights[0] : 1;
  const gainG = Array.isArray(highlights) ? highlights[1] : 1;
  const gainB = Array.isArray(highlights) ? highlights[2] : 1;

  // Convert denoise: Sati 0..1 to Tank 0..100
  const denoiseEnabled = denoise.enabled ?? false;
  const denoiseStrength = denoise.strength ?? 0;
  const tankDenoise = denoiseEnabled ? Math.round(denoiseStrength * 100) : 0;

  return {
    exposure: exposure.ev ?? 0,
    contrast: tankContrast,
    highlights: 0, // No direct Sati equivalent
    shadows: 0, // No direct Sati equivalent
    whites: 0, // No direct Sati equivalent
    blacks: 0, // No direct Sati equivalent
    clarity: 0, // Sati doesn't have clarity
    temperature: tankTemperature,
    tint: tankTint,
    sharpening: {
      amount: sharpen.amount ?? 0,
      radius: sharpen.radius ?? 0.5,
    },
    color_grading: {
      lift: { r: liftR, g: liftG, b: liftB },
      gamma: { r: gammaR, g: gammaG, b: gammaB },
      gain: { r: gainR, g: gainG, b: gainB },
    },
    denoise: tankDenoise,
  };
}

/**
 * Convert Tank adjustments to Sati preset format for re-export.
 *
 * @param {Object} tankAdjustments - Tank adjustments object
 * @param {Object} preserveNative - Optional sati_native to preserve lossless values
 * @returns {Object} Sati preset dictionary
 */
export function tankToSati(tankAdjustments, preserveNative = null) {
  const tank = tankAdjustments || {};
  const native = preserveNative || {};
  const nativeTonemap = native.tonemap || {};
  const nativeSharpen = native.sharpen || {};
  const nativeVibrance = native.vibrance || {};
  const nativeDenoise = native.denoise || {};

  // Convert contrast: Tank 0 = neutral, maps to Sati 1.0
  const satiContrast = 1.0 + ((tank.contrast || 0) / 100) * CONTRAST_MAX_SATI;

  // Convert white balance: Tank -100..100 to Sati -1..1
  const satiTemperature = (tank.temperature || 0) / 100;
  const satiTint = (tank.tint || 0) / 100;

  // Convert color grading back to color balance
  const cg = tank.color_grading || {};
  const lift = cg.lift || { r: 0, g: 0, b: 0 };
  const gamma = cg.gamma || { r: 1, g: 1, b: 1 };
  const gain = cg.gain || { r: 1, g: 1, b: 1 };

  // Tank lift (offset) -> Sati shadows (multiplier)
  let shadows = [1.0 + lift.r, 1.0 + lift.g, 1.0 + lift.b];
  const midtones = [gamma.r, gamma.g, gamma.b];
  let highlights = [gain.r, gain.g, gain.b];

  // Incorporate highlights/shadows sliders as brightness adjustment
  if (tank.highlights) {
    const hlAdj = 1.0 + tank.highlights / 200;
    highlights = highlights.map((v) => v * hlAdj);
  }
  if (tank.shadows) {
    const shAdj = 1.0 + tank.shadows / 200;
    shadows = shadows.map((v) => v * shAdj);
  }

  // Convert denoise: Tank 0..100 to Sati 0..1
  const satiDenoiseStrength = (tank.denoise || 0) / 100;
  const satiDenoiseEnabled = (tank.denoise || 0) > 0;

  return {
    version: 1,
    name: 'Tank Export',
    description: 'Settings exported from Tank editor',
    exposure: { ev: tank.exposure || 0 },
    tonemap: {
      method: nativeTonemap.method || 'sigmoid',
      contrast: satiContrast,
      skew: nativeTonemap.skew ?? 0,
      midpoint: nativeTonemap.midpoint ?? 0.5,
    },
    white_balance: {
      temperature: satiTemperature,
      tint: satiTint,
    },
    color_balance: {
      shadows: shadows,
      midtones: midtones,
      highlights: highlights,
    },
    vibrance: {
      vibrance: nativeVibrance.vibrance ?? 0,
      saturation: nativeVibrance.saturation ?? 0,
    },
    sharpen: {
      amount: tank.sharpening?.amount ?? 0,
      radius: tank.sharpening?.radius ?? 0.5,
      threshold: nativeSharpen.threshold ?? 0,
    },
    denoise: {
      enabled: satiDenoiseEnabled,
      model: nativeDenoise.model || 'nafnet',
      strength: satiDenoiseStrength,
    },
  };
}

/**
 * Extract sati_native from applied_settings for roundtrip preservation.
 *
 * @param {Object} appliedSettings - The applied_settings from meta.json
 * @returns {Object|null} The sati_native section or null
 */
export function extractSatiNative(appliedSettings) {
  if (!appliedSettings || !appliedSettings.sati_native) {
    return null;
  }
  return appliedSettings.sati_native;
}

/**
 * Check if adjustments differ from the Sati-applied settings.
 *
 * @param {Object} currentAdjustments - Current Tank adjustments
 * @param {Object} appliedSettings - Original applied_settings from meta.json
 * @returns {boolean} True if user has modified settings
 */
export function hasUserModifications(currentAdjustments, appliedSettings) {
  if (!appliedSettings || !appliedSettings.tank_format) {
    // No original settings, any non-default is a modification
    return true;
  }

  const original = appliedSettings.tank_format;
  const current = currentAdjustments;

  // Compare basic numeric values
  const basicKeys = [
    'exposure',
    'contrast',
    'highlights',
    'shadows',
    'whites',
    'blacks',
    'clarity',
    'temperature',
    'tint',
    'denoise',
  ];

  for (const key of basicKeys) {
    if ((current[key] ?? 0) !== (original[key] ?? 0)) {
      return true;
    }
  }

  // Compare sharpening
  if (
    (current.sharpening?.amount ?? 0) !== (original.sharpening?.amount ?? 0) ||
    (current.sharpening?.radius ?? 0.5) !== (original.sharpening?.radius ?? 0.5)
  ) {
    return true;
  }

  // Compare color grading (simplified check)
  const cgCurrent = current.color_grading || {};
  const cgOriginal = original.color_grading || {};

  for (const zone of ['lift', 'gamma', 'gain']) {
    for (const channel of ['r', 'g', 'b']) {
      const defaultVal = zone === 'lift' ? 0 : 1;
      if (
        (cgCurrent[zone]?.[channel] ?? defaultVal) !==
        (cgOriginal[zone]?.[channel] ?? defaultVal)
      ) {
        return true;
      }
    }
  }

  return false;
}
